/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { 
  HeartPulse,
  Filter
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Login } from './components/Login';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './components/Dashboard';
import { LiveConsult } from './components/LiveConsult';
import { LabAnalysis } from './components/LabAnalysis';
import { AuditLogs } from './components/AuditLogs';

export default function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<string | null>(null);

  const handleLogin = (username: string) => {
    setIsAuthenticated(true);
    setUser(username);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUser(null);
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div 
        key="main-app"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden"
      >
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col shrink-0">
          <Sidebar 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            setIsMobileMenuOpen={setIsMobileMenuOpen} 
            user={user} 
            handleLogout={handleLogout} 
          />
        </aside>

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <aside className={`fixed inset-y-0 left-0 w-64 bg-white z-50 lg:hidden transform transition-transform duration-300 ease-in-out flex flex-col ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <Sidebar 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            setIsMobileMenuOpen={setIsMobileMenuOpen} 
            user={user} 
            handleLogout={handleLogout} 
          />
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Mobile Header */}
          <header className="lg:hidden bg-white border-b border-slate-200 p-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="bg-raspberry-600 p-1.5 rounded text-white">
                <HeartPulse size={18} />
              </div>
              <span className="font-bold text-slate-900 text-sm">Cardio-Onc AI</span>
            </div>
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
            >
              <Filter size={20} />
            </button>
          </header>

          <main className="flex-1 overflow-y-auto p-4 md:p-8">
            {activeTab === 'dashboard' && <Dashboard />}
            {activeTab === 'live-consult' && <LiveConsult isAuthenticated={isAuthenticated} />}
            {activeTab === 'lab-analysis' && <LabAnalysis isAuthenticated={isAuthenticated} />}
            {activeTab === 'audit-logs' && <AuditLogs />}
          </main>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
